// Content for the press page (Block A: featured article, Block B: press kit).
// Source: Briefing Thang - Press page ecoshift.eco (v2), Trello #208.
// Rights: Roularta written authorization (22/07/2026) covers the PDF as-is with credit,
// and the EN/NL/DE translations with credit + "not reviewed by the editorial team" notice.
// Do not rebuild the French article as HTML: FR visitors get the PDF + link to the original.

// Single canonical URLs — the page content follows the site-wide language
// dropdown (i18n.language / localStorage "lang"), same as every other page,
// instead of encoding the language in the path.
export const pressPath = "/press";
export const articlePath = "/press/article";

const pdfHref = `${process.env.PUBLIC_URL}/assets/press/trends-tendances-quand-dame-nature-siege-au-ca-2026-02-05.pdf`;
const originalArticleUrl =
  "https://trends.levif.be/entreprises/quand-dame-nature-siege-au-ca-voici-les-pionniers-belges-de-la-gouvernance-regenerative/";
const trendsLogo = `${process.env.PUBLIC_URL}/assets/press/trends-tendances-logo.jpg`;
const ecoshiftLogo = `${process.env.PUBLIC_URL}/assets/press/ecoshift-logo-press-kit.png`;
const founderPhoto = `${process.env.PUBLIC_URL}/assets/press/baudouin-denis-cofounder-ecoshift.png`;

const trendsTendancesArticle = {
  id: "trends-tendances-2026-02-05",
  sourceName: "Trends-Tendances",
  sourceLogo: trendsLogo,
  dateISO: "2026-02-05",
  pdfHref,
  originalArticleUrl,
  credit: {
    fr: "Trends-Tendances, 5 février 2026, Caroline Lallemand",
    en: "Trends-Tendances, 5 February 2026, Caroline Lallemand",
    nl: "Trends-Tendances, 5 februari 2026, Caroline Lallemand",
    de: "Trends-Tendances, 5. Februar 2026, Caroline Lallemand",
  },
  dateLabel: {
    fr: "5 février 2026",
    en: "5 February 2026",
    nl: "5 februari 2026",
    de: "5. Februar 2026",
  },
  headline: {
    fr: "Quand Dame Nature siège au CA",
    en: "When Mother Nature sits on the board",
    nl: "Als Moeder Natuur in de raad van bestuur zetelt",
    de: "Wenn Mutter Natur im Verwaltungsrat sitzt",
  },
  summary: {
    fr: "Trends-Tendances consacre une analyse de quatre pages aux pionniers belges de la gouvernance régénérative. EcoShift y figure comme première entreprise belge à avoir intégré la Nature comme actionnaire et administratrice dès sa création, avec droits de veto.",
    en: "Trends-Tendances devotes a four-page analysis to the Belgian pioneers of regenerative governance. EcoShift features as the first Belgian company to make Nature a shareholder and board member from its creation, with veto rights.",
    nl: "Trends-Tendances wijdt een analyse van vier pagina's aan de Belgische pioniers van regeneratief bestuur. EcoShift komt erin voor als eerste Belgische onderneming die de Natuur vanaf haar oprichting als aandeelhouder en bestuurder heeft opgenomen, met vetorecht.",
    de: "Trends-Tendances widmet den belgischen Pionieren der regenerativen Unternehmensführung eine vierseitige Analyse. EcoShift erscheint darin als erstes belgisches Unternehmen, das die Natur von seiner Gründung an als Aktionärin und Verwaltungsratsmitglied integriert hat, mit Vetorecht.",
  },
  pullQuote: {
    fr: "Maintenant que la nature est intégrée au board, on se demande d'emblée comment elle orienterait nos décisions.",
    en: "Now that nature is on the board, we immediately ask ourselves how it would steer our decisions.",
    nl: "Nu de natuur deel uitmaakt van de board, vragen we ons meteen af hoe zij onze beslissingen zou sturen.",
    de: "Jetzt, wo die Natur im Board integriert ist, fragen wir uns von vornherein, wie sie unsere Entscheidungen lenken würde.",
  },
  pullQuoteAuthor: {
    fr: "Baudouin Denis, EcoShift",
    en: "Baudouin Denis, EcoShift",
    nl: "Baudouin Denis, EcoShift",
    de: "Baudouin Denis, EcoShift",
  },
};

// Newest first. Today there is only one article: it becomes the hero (Block A).
// When a new one is added, unshift it here — older entries automatically stack
// below as compact rows instead of the hero.
export const pressArticles = [trendsTendancesArticle];

export const pageChrome = {
  fr: {
    pageTitle: "Ils parlent d'EcoShift",
    metaDescription:
      "Trends-Tendances consacre une analyse de quatre pages aux pionniers belges de la gouvernance régénérative, avec EcoShift comme première entreprise belge à avoir intégré la Nature comme actionnaire et administratrice.",
    kicker: "Ils parlent d'EcoShift",
    featuredLabel: "Article à la une",
    olderLabel: "Autres articles",
    readPdf: "Lire l'article (FR, PDF)",
    readOriginal: "Lire l'original sur trends.levif.be",
    readTranslation: null,
    creditPrefix: "Source :",
    pressKitTitle: "Kit presse",
    pressKitIntro: "Pour les journalistes qui souhaitent écrire sur EcoShift.",
    aboutTitle: "À propos d'EcoShift",
    downloadLogo: "Télécharger le logo EcoShift",
    downloadPhoto: "Télécharger la photo du fondateur",
    pressContactLabel: "Contact presse",
  },
  en: {
    pageTitle: "In the press",
    metaDescription:
      "Trends-Tendances devotes a four-page analysis to the Belgian pioneers of regenerative governance, featuring EcoShift as the first Belgian company to make Nature a shareholder and board member.",
    kicker: "In the press",
    featuredLabel: "Featured article",
    olderLabel: "More articles",
    readPdf: "Read the article (FR, PDF)",
    readOriginal: "Read the original on trends.levif.be",
    readTranslation: "Read the translation",
    creditPrefix: "Source:",
    pressKitTitle: "Press kit",
    pressKitIntro: "For journalists who will write about EcoShift.",
    aboutTitle: "About EcoShift",
    downloadLogo: "Download EcoShift logo",
    downloadPhoto: "Download founder photo",
    pressContactLabel: "Press contact",
  },
  nl: {
    pageTitle: "In de pers",
    metaDescription:
      "Trends-Tendances wijdt een analyse van vier pagina's aan de Belgische pioniers van regeneratief bestuur, met EcoShift als eerste Belgische onderneming die de Natuur als aandeelhouder en bestuurder heeft opgenomen.",
    kicker: "In de pers",
    featuredLabel: "Uitgelicht artikel",
    olderLabel: "Meer artikels",
    readPdf: "Lees het artikel (FR, PDF)",
    readOriginal: "Lees het origineel op trends.levif.be",
    readTranslation: "Lees de vertaling",
    creditPrefix: "Bron:",
    pressKitTitle: "Perskit",
    pressKitIntro: "Voor journalisten die over EcoShift willen schrijven.",
    aboutTitle: "Over EcoShift",
    downloadLogo: "EcoShift-logo downloaden",
    downloadPhoto: "Foto van de oprichter downloaden",
    pressContactLabel: "Perscontact",
  },
  de: {
    pageTitle: "In der Presse",
    metaDescription:
      "Trends-Tendances widmet den belgischen Pionieren der regenerativen Unternehmensführung eine vierseitige Analyse, in der EcoShift als erstes belgisches Unternehmen mit der Natur als Aktionärin und Verwaltungsratsmitglied vorgestellt wird.",
    kicker: "In der Presse",
    featuredLabel: "Vorgestellter Artikel",
    olderLabel: "Weitere Artikel",
    readPdf: "Artikel lesen (FR, PDF)",
    readOriginal: "Original auf trends.levif.be lesen",
    readTranslation: "Übersetzung lesen",
    creditPrefix: "Quelle:",
    pressKitTitle: "Pressekit",
    pressKitIntro: "Für Journalisten, die über EcoShift schreiben möchten.",
    aboutTitle: "Über EcoShift",
    downloadLogo: "EcoShift-Logo herunterladen",
    downloadPhoto: "Foto des Gründers herunterladen",
    pressContactLabel: "Presskontakt",
  },
};

export const aboutBoilerplate = {
  fr: "EcoShift est une plateforme digitale bruxelloise qui accélère la transition vers la consommation écopositive pour le bien-être de tous. Fondée en juillet 2025, EcoShift est la première entreprise belge dont la Nature est actionnaire et membre du conseil d'administration. Lancement public : 10 septembre 2026.",
  en: "EcoShift is a Brussels-based digital platform that accelerates the transition to eco-positive consumption for the well-being of all. Founded in July 2025, EcoShift is the first Belgian company with Nature as a shareholder and board member. Public launch: 10 September 2026.",
  nl: "EcoShift is een Brussels digitaal platform dat de transitie naar ecopositieve consumptie versnelt, voor het welzijn van iedereen. EcoShift werd opgericht in juli 2025 en is de eerste Belgische onderneming met de Natuur als aandeelhouder en lid van de raad van bestuur. Publieke lancering: 10 september 2026.",
  de: "EcoShift ist eine Brüsseler digitale Plattform, die den Übergang zu ökopositivem Konsum beschleunigt, für das Wohl aller. EcoShift wurde im Juli 2025 gegründet und ist das erste belgische Unternehmen mit der Natur als Aktionärin und Mitglied des Verwaltungsrats. Öffentlicher Start: 10. September 2026.",
};

export const pressAssets = {
  ecoshiftLogo,
  founderPhoto,
};

export const pressContactEmail = "baudouin.denis@ecoshift.eco";
